import React, { useState, useEffect } from 'react';
import { certificationService } from '../services/certificationService';
import { toast } from 'react-toastify';

const Certifications = () => {
  const [certifications, setCertifications] = useState([]);
  const [expiringReport, setExpiringReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [certsData, expiringData] = await Promise.all([
        certificationService.getAll(),
        certificationService.getExpiring(30)
      ]);
      setCertifications(certsData.certifications || []);
      setExpiringReport(expiringData);
    } catch (err) {
      toast.error('Failed to load certifications');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  const getDaysUntilExpiry = (date) => {
    const diff = new Date(date) - new Date();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getExpiryClass = (date) => {
    const days = getDaysUntilExpiry(date);
    if (days < 0) return 'expired';
    if (days <= 7) return 'urgent';
    if (days <= 30) return 'warning';
    return 'ok';
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Certifications</h1>
        <button className="btn-primary">+ Add Certification</button>
      </div>

      {/* Expiring Summary */}
      {expiringReport && (
        <div className="alert-summary">
          <div className="alert-box urgent">
            <span className="count">{expiringReport.urgent?.count || 0}</span>
            <span className="label">Urgent (≤7 days)</span>
          </div>
          <div className="alert-box warning">
            <span className="count">{expiringReport.warning?.count || 0}</span>
            <span className="label">Warning (8-14 days)</span>
          </div>
          <div className="alert-box upcoming">
            <span className="count">{expiringReport.upcoming?.count || 0}</span>
            <span className="label">Upcoming (15-30 days)</span>
          </div>
        </div>
      )}

      <div className="tabs">
        <button 
          className={activeTab === 'all' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('all')}
        >
          All Certifications
        </button>
        <button 
          className={activeTab === 'expiring' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('expiring')}
        >
          Expiring Soon
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading certifications...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Type</th>
                <th>Number</th>
                <th>Issue Date</th>
                <th>Expiration</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(activeTab === 'expiring' 
                ? [...(expiringReport?.urgent?.certifications || []), 
                   ...(expiringReport?.warning?.certifications || []),
                   ...(expiringReport?.upcoming?.certifications || [])]
                : certifications
              ).map(cert => (
                <tr key={cert.id} className={getExpiryClass(cert.expiration_date)}>
                  <td>{cert.employee_name}</td>
                  <td>{cert.certification_type?.replace(/_/g, ' ')}</td>
                  <td>{cert.certification_number || '-'}</td>
                  <td>{formatDate(cert.issue_date)}</td>
                  <td>
                    <span className={`expiry-badge ${getExpiryClass(cert.expiration_date)}`}>
                      {formatDate(cert.expiration_date)}
                      {getDaysUntilExpiry(cert.expiration_date) <= 30 && (
                        <small> ({getDaysUntilExpiry(cert.expiration_date)} days)</small>
                      )}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${cert.status}`}>
                      {cert.status}
                    </span>
                  </td>
                  <td>
                    <button className="btn-small">View</button>
                    <button className="btn-small">Renew</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Certifications;
